import styled, { css } from 'styled-components'
import React, { useMemo, useState } from 'react'
import { ReactHookFc, ReactHookForm } from 'src/components/common/ReactHookForm'
import { Noto_body3_b12, Noto_body3_r12 } from 'src/styles/fonts/NotoSansKrFonts'
import { Icon } from 'src/assets/svg/Icon'
import { Typography } from 'src/components/common/Typography'
import { Container } from 'src/components/common/Container'
import Button, { ButtonOption4 } from 'src/components/common/Button'
import { useFormContext, SubmitHandler, SubmitErrorHandler } from 'react-hook-form'
import { FormObjectType, JoinTermsAgreeItemsType } from 'src/types/react-hook-form'
import RenderIf from 'src/components/common/RenderIf'

export interface JoinTermsPageProps {
  registerAttribute: Record<keyof JoinTermsAgreeItemsType, Partial<FormObjectType>>
  joinSubmitPending: boolean
  joinTermsOnSubmit: SubmitHandler<JoinTermsAgreeItemsType>
  joinTermsOnError: SubmitErrorHandler<JoinTermsAgreeItemsType>
  joinTermsOpenModalHandler: ({ t, children }: { t: string; children: string }) => void
}

const JOIN_SERVICE_NOTICE_ITEMS: React.ReactNode[] = [
  <>
    <strong>본 서비스는 ㈜데이터유니버스가 운영합니다. 고객센터 1855-4531(이용, 해지문의)</strong>
  </>,
  <>
    <strong>본 서비스는 청약철회가 불가능한 상품입니다.</strong>
  </>,
  <>
    <strong>서비스를 이용할 때 가입한 요금제에 따라 데이터 요금이 발생할 수 있습니다.</strong>
  </>,
  <>
    휴대폰도난결제 보상 보험은 가입 다음날 0시부터 제공되며, 자기부담금 20만원(1회 사고당)이
    발생합니다. 해당 보험은 1인 1사고당 보험한도액 100만원 내 보상하며, 증권상 최대 보상한도액 초과
    시 보험금이 지급되지 않을 수 있습니다.
  </>,
  <>
    본 서비스는 PC 및 모바일 브라우저에서 웹사이트에 접속하여 서비스 이용이 가능하므로 대부분의
    휴대폰 OS 및 단말기 제약이 없습니다.
  </>,
  <>
    서비스 내 한정 수량으로 판매되는 핫딜 쿠폰은 회차당 1인 1개, 월 최대 10개까지 구매 가능하며,
    판매 정책은 추후 변경될 수 있습니다.
  </>,
  <>홈페이지 주소 : https://www.couponwallet.co.kr/</>,
  <>전화번호 : 1855-4531, 이메일 : cs_coupon@datau.co.kr</>,
  <>사업자등록번호 : 382-86-01230 / 통신판매업신고 : 제2020-서울영등포-1315호</>,
]

const JoinTermsPageView = ({
  joinSubmitPending,
  registerAttribute,
  joinTermsOnSubmit,
  joinTermsOnError,
  joinTermsOpenModalHandler,
}: JoinTermsPageProps) => {
  const { handleSubmit } = useFormContext<JoinTermsAgreeItemsType>()
  const [accordionOpen, setAccordionOpen] = useState(false)

  const registerAttributeList = useMemo(
    () => Object.values(registerAttribute).filter(({ tag }) => tag && tag !== 'allTerms'),
    [registerAttribute],
  )

  const requiredTermsList = useMemo(
    () =>
      registerAttributeList.filter(
        (attr) => attr.register_options?.required === true && !!attr.labelValue,
      ),
    [registerAttributeList],
  )

  const optionalTermsList = useMemo(
    () =>
      registerAttributeList.filter(
        (attr) => attr.register_options?.required !== true && !!attr.labelValue,
      ),
    [registerAttributeList],
  )

  const handleJoinTermsAgreeSubmit = handleSubmit(joinTermsOnSubmit, joinTermsOnError)

  const renderTermRow = (attr: Partial<FormObjectType>) => {
    const { t, children } = attr.modalData!

    return (
      <RenderIf when={!!attr.labelValue}>
        <S.TermRow>
          <ReactHookFc.CheckBox
            {...attr}
            bottomChildren={
              <S.TermsRowLine>
                <S.TermsLabel $fontStyle={Noto_body3_r12}>{attr.labelValue}</S.TermsLabel>
                <S.TermsDetailArrow
                  onClick={() => joinTermsOpenModalHandler({ t: t!, children: children! })}
                >
                  <Icon.Arrow size="16" rotate="right" color="var(--black-50)" />
                </S.TermsDetailArrow>
              </S.TermsRowLine>
            }
          />
        </S.TermRow>
      </RenderIf>
    )
  }

  return (
    <S.JoinTermsContainer $option="flex_column_space_between" $width="calc(100% - 40px)">
      <Container $option="flex_column" $row_gap="30px">
        <ReactHookForm onSubmit={handleJoinTermsAgreeSubmit} style={S.JoinTermsWrap}>
          <S.AccordionShell>
            <S.AccordionHeaderBar $option="flex_row" $width="100%">
              <ReactHookFc.CheckBox
                {...registerAttribute.allTerms}
                bottomChildren={
                  <S.AccordionTitle>{registerAttribute.allTerms.labelValue}</S.AccordionTitle>
                }
              />
              <S.AccordionChevron
                type="button"
                aria-expanded={accordionOpen}
                aria-label={accordionOpen ? '필수 약관 접기' : '필수 약관 펼치기'}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setAccordionOpen((v) => !v)
                }}
              >
                <Icon.Arrow
                  size="16"
                  rotate={accordionOpen ? 'top' : 'bottom'}
                  color="var(--black-50)"
                />
              </S.AccordionChevron>
            </S.AccordionHeaderBar>
            <S.AccordionPanel $open={accordionOpen}>
              {requiredTermsList.map((attr) => (
                <React.Fragment key={String(attr.tag)}>{renderTermRow(attr)}</React.Fragment>
              ))}
            </S.AccordionPanel>
          </S.AccordionShell>
          {optionalTermsList.map((attr) => (
            <React.Fragment key={String(attr.tag)}>{renderTermRow(attr)}</React.Fragment>
          ))}
        </ReactHookForm>
      </Container>
      <S.BottomContents $option="flex_column" $row_gap="10px">
        <Button
          option={ButtonOption4}
          onClickFn={handleJoinTermsAgreeSubmit}
          disabled={joinSubmitPending}
        >
          휴대폰쿠폰지갑 유료부가서비스 가입
        </Button>
        <S.BottomText>
          SKT, KT, LGU+ 통신요금에 포함되어
          <br />
          매월 부가세포함 5,500원 부과됩니다.
        </S.BottomText>
      </S.BottomContents>
      <S.FooterContents>
        <S.FooterNoticeList>
          <Typography $style={Noto_body3_b12} $color="var(--black-70)">
            유의사항
          </Typography>
          {JOIN_SERVICE_NOTICE_ITEMS.map((node, idx) => (
            <S.FooterNoticeItem key={idx}>{node}</S.FooterNoticeItem>
          ))}
        </S.FooterNoticeList>
      </S.FooterContents>
    </S.JoinTermsContainer>
  )
}

export default JoinTermsPageView
const S = {
  JoinTermsContainer: styled(Container)`
    width: 100%;
    max-width: 360px;
    padding: 30px 0;
    flex: 1;
  `,
  BottomContents: styled(Container)`
    padding-top: 20px;
    text-align: center;
  `,
  JoinTermsWrap: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,
  AccordionShell: styled.div`
    width: 100%;
    background: var(--white);
    box-sizing: border-box;
  `,
  AccordionHeaderBar: styled(Container)`
    align-items: center;
    column-gap: 14px;
    box-sizing: border-box;
    width: 100%;
    padding: 16px;
    background: var(--black-4);
    border-radius: 12px;
  `,
  AccordionPanel: styled.div<{ $open: boolean }>`
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex-direction: column;
    gap: 18px;
    width: 100%;
    box-sizing: border-box;
    background: var(--white);
    padding: 20px 0px 0px;
  `,
  AccordionTitle: styled(Typography)`
    ${Noto_body3_b12};
    flex: 1;
    min-width: 0;
    text-align: left;
  `,
  AccordionChevron: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
  `,
  TermRow: styled(Container)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    gap: 8px;
    padding: 0 14px;
  `,
  TermsRowLine: styled(Container)`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    gap: 8px;
  `,
  TermsDetailArrow: styled(Container)`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
  `,
  TermsLabel: styled(Typography)`
    flex: 1;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
  `,
  BottomText: styled(Typography)`
    word-break: keep-all;
    ${Noto_body3_b12};
    color: var(--red-50);
  `,
  FooterContents: styled(Container)`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 360px;
    min-width: 0;
    margin-top: 20px;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 20px;
    background: var(--black-2);
  `,
  FooterNoticeList: styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    width: 100%;
  `,
  FooterNoticeItem: styled.li`
    position: relative;
    padding-left: 24px;
    margin: 0;
    min-width: 0;
    color: var(--black-70);
    ${Noto_body3_r12};
    word-break: break-all;

    & strong {
      font-weight: 800;
    }

    &::before {
      content: '';
      position: absolute;
      left: 8px;
      top: 10px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: var(--black-70);
    }
  `,
}
